/** @file sampleNodeKey: a node's key value at any frame of a clip, interpolated in key space (authoring). */
import type { Clip, ClipKey, NodeKey } from '@zilo/pose3d-format/types/clip';
import { easeAt } from '@zilo/pose3d-pose/time/easeAt';
import { holdFrame } from '@zilo/pose3d-pose/time/holdFrame';
import { discreteFields } from './discreteFields';
import { lerpNodeKey } from './lerpNodeKey';

/** The eased progress from key `a` to key `b` at `frame`, with the node's (else the key's) ease and hold. */
function progress(a: ClipKey, av: NodeKey, b: ClipKey, frame: number): number {
  const at = holdFrame(frame, a.frame, av.hold ?? a.hold ?? 1);
  return easeAt(av.ease ?? a.ease ?? 'linear', (at - a.frame) / (b.frame - a.frame));
}

/**
 * Between the last key at or before `frame` naming `node` (a) and the next one (b): `t` = ease of
 * (holdFrame(frame) − a) / (b − a) with the node's (else the key's) `ease` and `hold`; discrete fields come from a.
 * Before the first key → `{}`; after the last → a's values. No loop wrap; `evaluate` is the playback reference.
 */
export function sampleNodeKey(clip: Pick<Clip, 'keys'>, node: string, frame: number): NodeKey {
  const named = clip.keys.filter((k) => Object.hasOwn(k.nodes, node));
  const a = named.filter((k) => k.frame <= frame).at(-1);
  const av = a?.nodes[node];
  if (a === undefined || av === undefined) return {};
  const b = named.find((k) => k.frame > a.frame);
  const bv = b?.nodes[node] ?? av;
  const t = b === undefined || frame === a.frame ? 0 : progress(a, av, b, frame);
  return { ...lerpNodeKey(av, bv, t), ...discreteFields(av) };
}
